
import Vue from 'vue'
import { namespace } from 'vuex-class'
import { LoadState } from '@/utils/types'
import { Component } from 'vue-property-decorator'
import InfoBoxWarning from '@components/shared/ui/InfoBoxWarning.vue'
import { TripLog } from '@/generated-clients/myTrips'
import { CarHealth } from '@clients/bff';
import CarHealthAggregateState from '@components/car-health/CarHealthAggregateState.vue'
import CarHealthExportModal from '@components/car-health/CarHealthExportModal.vue'
import AppPage from '@components/new-styleguide/AppPage.vue';
import AppContent from '@components/new-styleguide/AppContent.vue';
import AppVehicleHeader from '@components/new-styleguide/AppVehicleHeader.vue';
import TripStatisticBarItem from '@components/new-styleguide/TripStatisticBarItem.vue';
import CarHealthTabs, { Tab } from '@components/car-health/CarHealthTabs.vue';
import SpeedIcon from '@components/icons/summary/icon-tachometer.svg';
import FuelIcon from '@components/icons/icon-fuel-tank.svg';
import { formatTripOdometer } from '@util/odometerService';
import { formatLocale } from '@/utils/numberFormat'

const TriplogModule = namespace('Triplog');
const CarHealthModule = namespace('CarHealth');

@Component({
  name: 'myCarHealth',
  components: {
    AppPage,
    AppContent,
    AppVehicleHeader,
    InfoBoxWarning,
    CarHealthAggregateState,
    CarHealthExportModal,
    TripStatisticBarItem,
    CarHealthTabs,
    SpeedIcon,
    FuelIcon
  },
})
export default class CarHealthScreen extends Vue {
  @TriplogModule.Mutation('RESET_GET_TRIPS')
  private resetGetTrips;

  @TriplogModule.Mutation('RESET_TRIP_ERRORS')
  private resetTripErrors;

  @CarHealthModule.Action('fetchCarHealth')
  private fetchCarHealth;

  @CarHealthModule.Action('checkMaintenanceItem')
  private checkMaintenanceItem;

  @CarHealthModule.Getter('getCarHealth')
  private carHealth: CarHealth;

  @CarHealthModule.Getter('getCarHealthStatus')
  private carHealthStatus: LoadState;

  @TriplogModule.Getter('getVehiclesStatus')
  private vehiclesStatus: LoadState;

  @TriplogModule.Action('fetchVehicles')
  private fetchVehicles;

  @TriplogModule.Getter('getVehicles')
  private vehicles: TripLog[];

  @TriplogModule.Getter('getSelectedTriplog')
  private selectedTriplog: TripLog;

  @TriplogModule.Getter('getFuelUnit')
  private fuelUnit: string

  @TriplogModule.Action('fetchLatestTrip')
  private fetchLatestTrip;

  private activeTab: Tab = 'warnings';

  private showExportModal: boolean = false;

  async created() {
    if (this.vehiclesStatus === LoadState.Success) {
      this.setActiveTab();
      this.handleTriplogInput(false);
    }
  }

  private async handleTriplogInput(resetTab: boolean = true) {
    if (this.selectedTriplog) {
      if (resetTab) {
        this.activeTab = 'warnings'
      }

      await this.fetchCarHealth({ vehicleId: this.selectedTriplog.vehicle.id });
      this.setActiveTab();
    }
  }

  private setActiveTab() {
    if (this.carHealthStatus !== LoadState.Success) {
      this.activeTab = 'warnings';
      return;
    }

    const { warnings, recommendations } = this.carHealth.healthIssues;
    const { maintenanceItems } = this.carHealth;

    if (warnings.length === 0 && recommendations.length === 0 && maintenanceItems.length > 0) {
      this.activeTab = 'reminders';
    } else {
      this.activeTab = 'warnings';
    }
  }

  private openExport() {
    this.showExportModal = true;
  }

  get isLoading() {
    return this.carHealthStatus === LoadState.Loading
        || this.vehiclesStatus === LoadState.Loading
        || !this.selectedTriplog;
  }

  get formattedFuelLevel() {
    return this.fuelLevel ? formatLocale(this.fuelLevel, 2) : '---';
  }

  get formattedOdometer() {
    return this.isLoading ? '---' : formatTripOdometer(this.carHealth.odometer).toString();
  }

  get errorLoading() {
    return this.carHealthStatus === LoadState.Error || this.vehiclesStatus === LoadState.Error;
  }

  get fuelLevel() {
    // In CC-3078 check if settings are measured or calculated

    if (this.isLoading) {
      return null;
    }

    const percentageFuelLevel = this.carHealth.lastFuelLevel;
    const tankCapacity = this.selectedTriplog.vehicleSettings.fuelCapacity;
    return tankCapacity && percentageFuelLevel ? (percentageFuelLevel / 100.0) * tankCapacity : null;
  }

  get fuelLevelUnit() {
    if (this.isLoading) {
      return null
    }

    return this.fuelUnit
  }

  get vehicleSmartConnected() {
    return this.selectedTriplog && this.selectedTriplog.smartConnected;
  }

  private handleFilterChanged(vehicleId: number) {
    this.resetGetTrips()
    this.resetTripErrors()
  }

  private handleMaintenanceItemChecked(maintenanceItem) {
    this.checkMaintenanceItem({ vehicleId: this.selectedTriplog.vehicle.id, maintenanceItem });
  }
}
